import { useCallback, useEffect, useMemo, useState } from 'react';
import { log } from '@utils/logging';
import { StripeAccountContextValues } from '@hooks/useStripeAccount';
import { stringFromError } from '@utils/utils';
import { initializeStripeInfo } from '@modules/api';
import { getStripeInfo, listenToStripeAccountChanges } from '@modules/supabase/payments';
import { StripeInfo } from '@type/db-types';

export default function useWatchStripeAccount(
    userId: string | undefined,
    source: string,
): StripeAccountContextValues {
    const [stripeInfo, setStripeInfo] = useState<StripeInfo | undefined | null>(undefined);
    const [error, setError] = useState<string>('');
    const watchingStripeAccount = !!stripeInfo; // most users will not need to watch their stripe account payment admins should though...

    const refreshStripeInfo = useCallback(() => {
        setError('');
        setStripeInfo(undefined);
        if (userId)
            getStripeInfo(userId)
                .then((info) => {
                    setStripeInfo(info);

                    if (!info) setError('No stripe info found');
                })
                .catch((e: unknown) => log(`[StripeAccountProvider]: ${stringFromError(e)}`));
    }, [userId]);

    // check if the user is already setup in stripe, but don't auto-initialize them
    useEffect(() => {
        // fetch stripe info once auth has loaded or the userId changes
        refreshStripeInfo();
    }, [refreshStripeInfo]);

    // Listen to stripe account changes
    useEffect(() => {
        if (userId) return listenToStripeAccountChanges(userId, setStripeInfo);
    }, [userId]);

    console.log(
        `[${source}] \n[useWatchStripeAccount] user: ${userId}, stripeUserId: ${stripeInfo?.id} watching: ${watchingStripeAccount}`,
    );

    const accountState = useMemo(
        () => ({
            loading: stripeInfo === undefined,
            stripeInfo,
            error,
            stripeAccountRequired: () => {
                if (userId && !watchingStripeAccount) {
                    initializeStripeInfo(userId)
                        .then((info) => console.log(`stripe account initialized`, info))
                        .catch((e: unknown) =>
                            log(`[StripeAccountProvider]: ${stringFromError(e)}`),
                        );

                    return listenToStripeAccountChanges(userId, setStripeInfo);
                }
            },
            watchingStripeAccount,
            refreshStripeInfo,
        }),
        [error, refreshStripeInfo, stripeInfo, userId, watchingStripeAccount],
    );
    return accountState;
}
